/**
 * "Proovi vestlust": try an AI call campaign's conversation as text before any
 * phone rings (ring R8, plan section 5.7).
 *
 * Two steps: name a company (`RehearsePicker`), then talk (`RehearseChat`). The
 * opening is fetched from the campaign's test call preview, so it is exactly
 * what a call with that company would say, and each reply is made on the server
 * with the call's prompt over the map on screen. Nothing rings, nothing is
 * saved, and every reply spends one Gemini request, which the dialog says.
 *
 * An alert campaign (ring R9) talks only about a defect found on the company's
 * website. A company without one has no opening to rehearse: the dialog says so
 * instead of starting a chat the server would refuse with `no_alert`.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, ArrowLeft, Loader2, MessagesSquare } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@wa/ui";
import type { FlowMapDoc } from "@wa/widgets/flow-map/model";
import { useT } from "../../../../../i18n/I18nProvider";
import { testCallError } from "../../../../../lib/aiOutbound";
import {
  previewAiOutboundTestCall,
  testCallNoAlert,
  type AiOutboundTestCallPreview,
  type AiTestCallTarget,
} from "../../../../../lib/aiTestCallPreview";
import { Note } from "../../../settings/panel/fields";
import { RehearseChat } from "./RehearseChat";
import { RehearsePicker } from "./RehearsePicker";

type Opening = { state: "loading" } | { state: "ready"; preview: AiOutboundTestCallPreview } | { state: "error"; key: string };

export function RehearseDialog({
  campaignId,
  doc,
  alert = false,
  onClose,
}: {
  campaignId: string;
  doc: FlowMapDoc;
  /** The saved campaign is an alert campaign: the picker asks for a company with a defect. */
  alert?: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const [target, setTarget] = useState<AiTestCallTarget | null>(null);
  const [opening, setOpening] = useState<Opening>({ state: "loading" });

  useEffect(() => {
    if (!target) return;
    let alive = true;
    setOpening({ state: "loading" });
    previewAiOutboundTestCall(target).then(
      (preview) => {
        if (alive) setOpening({ state: "ready", preview });
      },
      (err: unknown) => {
        if (alive) setOpening({ state: "error", key: testCallError(err).key });
      },
    );
    return () => {
      alive = false;
    };
  }, [target]);

  const preview = opening.state === "ready" ? opening.preview : null;
  const noAlert = testCallNoAlert(preview);
  const company = preview?.company.name || target?.name || preview?.company.domain || target?.domain || target?.regCode || "";

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[94dvh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessagesSquare className="size-4 text-primary" />
            {t("aiCalls.rehearse.title")}
            {target && company ? <span className="font-normal text-muted-foreground">- {company}</span> : null}
          </DialogTitle>
          <DialogDescription>{t("aiCalls.rehearse.intro")}</DialogDescription>
        </DialogHeader>

        {!target ? <RehearsePicker campaignId={campaignId} alert={alert} onPick={setTarget} /> : null}

        {target && opening.state === "loading" ? (
          <p className="crm-sub flex items-center gap-2 py-6 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {t("aiCalls.rehearse.chat.loadingOpening")}
          </p>
        ) : null}

        {target && opening.state === "error" ? (
          <p className="crm-alert">
            <AlertTriangle className="size-4 shrink-0" />
            {t(`aiOutbound.testCall.error.${opening.key}`, { cap: "", number: "" })}
          </p>
        ) : null}

        {target && noAlert ? (
          <Note tone="warn" live>
            {t("aiCalls.rehearse.error.no_alert")}
          </Note>
        ) : null}

        {target && preview && !noAlert ? (
          <RehearseChat key={`${target.regCode ?? ""}:${target.domain ?? ""}`} campaignId={campaignId} target={target} opening={preview.openingText} doc={doc} />
        ) : null}

        <DialogFooter className="flex-wrap gap-2">
          {target ? (
            <Button variant="ghost" onClick={() => setTarget(null)}>
              <ArrowLeft className="size-4" />
              {t("aiCalls.rehearse.chat.changeCompany")}
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose}>
            {t("aiCalls.rehearse.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
